import { css, Global } from "@emotion/react";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { formatInTimeZone, notEmpty } from "shared/lib/utils";

import { LiveBlogPaywalledContentClass } from "scmp-app/components/article/article-paywall-container/consts";
import { sortLiveContentsByCustomTime } from "scmp-app/components/article/article-render/live-article/helpers";
import type { ssrLiveContentsPreviewLiveArticle$key } from "scmp-app/queries/__generated__/ssrLiveContentsPreviewLiveArticle.graphql";

import { Authors, Body, Container, Header, Item, List, Time, Title } from "./styles";

export const ssrLiveContentsPreviewFragment = graphql`
  fragment ssrLiveContentsPreviewLiveArticle on LiveArticle {
    liveContents {
      customTime
      entityUuid
      title
      authors {
        name
      }
      body {
        text
      }
    }
  }
`;

type Props = {
  reference: ssrLiveContentsPreviewLiveArticle$key;
};

/**
 * Server-rendered live updates for verified search bots.
 *
 * Styled to mirror the interactive LiveArticleContentItem (content/item):
 * same header divider, time treatment, fonts and spacing — but hand-written
 * so the block stays free of client-only dependencies (scroll context,
 * provider widgets, rosetta). Rendered as a sibling of the paywall slot and
 * outside the client-only <ClientSideSuspense>/<PianoPaywall> subtree in
 * live-article/index.tsx, so it never suspends. Humans never receive this
 * block (the @include gate in articlePageQuery only fires for x-is-bot: 1
 * requests, and Varnish splits the bot/human HTML caches). Paywall visibility
 * is declared honestly via the article JSON-LD hasPart selector.
 *
 * IMPORTANT: do not remove this block from the DOM on hydrate —
 * JavaScript-rendering search bots rely on the text staying present. The
 * [data-ssr-hydrated] CSS rule hides it visually once the interactive timeline
 * replaces it, so users never see duplicated content.
 */
export const SsrLiveContentsPreview: FunctionComponent<Props> = ({ reference }) => {
  const liveArticle = useFragment(ssrLiveContentsPreviewFragment, reference);

  // Match the client timeline's ordering: newest update first, descending
  // customTime (see live-contents-sort for the shared helper).
  const contents = sortLiveContentsByCustomTime(liveArticle?.liveContents);

  if (contents.length === 0) return null;

  return (
    <Container className={LiveBlogPaywalledContentClass} data-ssr-live-contents>
      {/*
       * Injected here (not as a `[data-ssr-hydrated] &` rule on Container) on
       * purpose: `&` compiles to the server-side emotion class hash, which can
       * differ from the client hash after hydration — the rule would then
       * never match and the preview would leak to human users. The attribute
       * selector is hash-independent, and <Global> keeps the rule colocated
       * with the only component that renders the data-ssr-live-contents block.
       */}
      <Global
        styles={css`
          [data-ssr-hydrated] [data-ssr-live-contents] {
            display: none;
          }
        `}
      />
      <List>
        {contents.map((liveContent, index) => {
          const key = liveContent.entityUuid ?? index;
          const bodyParagraphs = (liveContent.body?.text ?? "")
            .split(/\r?\n+/)
            .map(line => line.trim())
            .filter(Boolean);
          return (
            <Item key={key}>
              <Header>
                {notEmpty(liveContent.customTime) && (
                  <Time>{formatInTimeZone(liveContent.customTime, "h:mma")}</Time>
                )}
              </Header>
              {notEmpty(liveContent.title) && <Title>{liveContent.title}</Title>}
              {(liveContent.authors ?? []).length > 0 && (
                <Authors>
                  From{" "}
                  {(liveContent.authors ?? [])
                    .map(author => author?.name ?? "")
                    .filter(Boolean)
                    .join(", ")
                    .replace(/(, )([^,]+)$/, " and $2")}
                </Authors>
              )}
              {bodyParagraphs.length > 0 && (
                <Body>
                  {bodyParagraphs.map((paragraph, paragraphIndex) => (
                    <p key={paragraphIndex}>{paragraph}</p>
                  ))}
                </Body>
              )}
            </Item>
          );
        })}
      </List>
    </Container>
  );
};

SsrLiveContentsPreview.displayName = "SsrLiveContentsPreview";
